import { Link, LinkTarget, LinkVariant } from '@ampcore/adel-react'
import React from 'react'

import { getBackgroundConfig } from './getBackgroundConfig'
import {
  getButtonClassNames,
  renderCertificateRates,
  renderEyebrowText,
  renderIconEyebrowImage,
  renderInnerContent,
  TwoColumnBannerProps
} from './TwoColumnBannerColumns'
import { TwoColumnBannerShell } from './TwoColumnBannerShell'

// Independent layout: each column flows on its own height rather than being
// forced onto a shared row grid. Used when a column holds media with intrinsic
// height — an image, table, video, or certificate rate circles. The column
// divs are rendered directly (no DisplayGrid extraction).
export const TwoColumnBannerIndependent = (props: TwoColumnBannerProps) => {
  const {
    psnAnalytics,
    textColor,
    leftIcon,
    leftIconColor,
    fillLeft,
    strokeLeft,
    leftEyebrowImage,
    leftEyebrowImageAlt,
    leftEyebrowText,
    leftPlaylistId,
    leftColumnContent,
    leftCertificateRates,
    leftPrimaryBtnText,
    leftPrimaryBtnUrl,
    leftPrimaryBtnTarget,
    leftPrimaryBtnAnalytics,
    leftTertiaryBtnText,
    leftTertiaryBtnUrl,
    leftTertiaryBtnTarget,
    leftTertiaryBtnAnalytics,
    leftBackgroundColor,
    rightIcon,
    rightIconColor,
    fillRight,
    strokeRight,
    rightEyebrowImage,
    rightEyebrowText,
    rightEyebrowImageAlt,
    rightPlaylistId,
    rightColumnContent,
    rightCertificateRates,
    rightPrimaryBtnText,
    rightPrimaryBtnUrl,
    rightPrimaryBtnTarget,
    rightPrimaryBtnAnalytics,
    rightTertiaryBtnText,
    rightTertiaryBtnUrl,
    rightTertiaryBtnTarget,
    rightTertiaryBtnAnalytics,
    rightBackgroundColor,
    btnColor,
    testId
  } = props

  const hasLeftColumnImage = leftColumnContent?.includes('<img')
  const hasRightColumnImage = rightColumnContent?.includes('<img')
  const hasAnyImage = hasLeftColumnImage || hasRightColumnImage
  const hasBothImages = hasLeftColumnImage && hasRightColumnImage
  const hasLeftColumnTable = leftColumnContent?.includes('<table')
  const hasRightColumnTable = rightColumnContent?.includes('<table')
  const hasVideo = !!(leftPlaylistId || rightPlaylistId)
  const hasAnyTable = !!(hasLeftColumnTable || hasRightColumnTable)

  const { primaryBtnClassName, tertiaryBtnClassName } =
    getButtonClassNames(btnColor)

  const hasCertificateRates = !!(leftCertificateRates || rightCertificateRates)

  const singleMedia =
    (hasLeftColumnImage && !hasRightColumnImage) ||
    (hasRightColumnImage && !hasLeftColumnImage) ||
    hasVideo ||
    hasAnyTable

  const classes = [
    'TwoColumnBanner-columns',
    hasCertificateRates && 'TwoColumnBanner-certificateRatesGrid',
    hasRightColumnImage &&
      !hasBothImages &&
      'TwoColumnBanner-columns--mdColumn2First',
    !hasCertificateRates && singleMedia && 'u-flexAlignItemsCenter'
  ]
    .filter(Boolean)
    .join(' ')

  const leftColumn = (
    <div
      className={`TwoColumnBanner-columnLeft ${leftBackgroundColor ? 'u-borderRadius24 u-padding24 ' + getBackgroundConfig(leftBackgroundColor).classes : ''}`}
      data-testid={testId ? `${testId}-left-column` : undefined}
    >
      {leftCertificateRates ? (
        renderCertificateRates('left', leftCertificateRates)
      ) : (
        <>
          {renderIconEyebrowImage({
            side: 'left',
            icon: leftIcon,
            iconColor: leftIconColor,
            fill: fillLeft,
            stroke: strokeLeft,
            eyebrowImage: leftEyebrowImage,
            eyebrowImageAlt: leftEyebrowImageAlt,
            containerClassName:
              'u-sm-paddingTop4 u-md-paddingTop8 u-paddingVert0',
            testId
          })}
          {renderEyebrowText({
            side: 'left',
            eyebrowText: leftEyebrowText,
            className: `TwoColumnBanner-eyebrowText Type-5 u-textEyebrow u-paddingVert8 u-sm-paddingRight0 u-md-paddingRight0 u-paddingRight40`,
            testId
          })}
          {renderInnerContent({
            side: 'left',
            containerClassName: [
              'TwoColumnBanner-innerContent u-paddingBottom8',
              !hasLeftColumnImage &&
                'u-sm-paddingRight0 u-md-paddingRight0 u-paddingRight40'
            ]
              .filter(Boolean)
              .join(' '),
            columnContent: leftColumnContent,
            hasColumnTable: hasLeftColumnTable,
            backgroundColor: leftBackgroundColor,
            textColor,
            descriptionPlaylistId: rightPlaylistId,
            descriptionContainerClassName:
              'TwoColumnBanner-videoDescriptionContainer',
            playerPlaylistId: leftPlaylistId,
            testId
          })}
          {(leftPrimaryBtnUrl || leftTertiaryBtnUrl) && (
            <div
              className={`u-sm-paddingBottom4 u-md-paddingBottom8 u-paddingBottom0 u-paddingTop32 ${
                !hasAnyImage ? 'u-sm-paddingBottom40 u-md-paddingBottom40' : ''
              }`}
            >
              {leftPrimaryBtnUrl && (
                <Link
                  variant={LinkVariant.PRIMARY}
                  name={leftPrimaryBtnText ?? ''}
                  url={leftPrimaryBtnUrl}
                  target={leftPrimaryBtnTarget as LinkTarget}
                  analyticsName={leftPrimaryBtnAnalytics}
                  className={primaryBtnClassName}
                  data-psn-analytics={psnAnalytics}
                  testId={testId ? `${testId}-primary-left-button` : undefined}
                />
              )}
              {leftTertiaryBtnUrl && (
                <Link
                  variant={LinkVariant.TERTIARY}
                  name={leftTertiaryBtnText ?? ''}
                  url={leftTertiaryBtnUrl}
                  target={leftTertiaryBtnTarget as LinkTarget}
                  analyticsName={leftTertiaryBtnAnalytics}
                  className={tertiaryBtnClassName}
                  data-psn-analytics={psnAnalytics}
                  testId={testId ? `${testId}-tertiary-left-button` : undefined}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  )

  const rightColumn = (
    <div
      className={`TwoColumnBanner-columnRight ${rightBackgroundColor ? 'u-borderRadius24 u-padding24 ' + getBackgroundConfig(rightBackgroundColor).classes : ''}`}
      data-testid={testId ? `${testId}-right-column` : undefined}
    >
      {rightCertificateRates ? (
        renderCertificateRates('right', rightCertificateRates)
      ) : (
        <>
          {renderIconEyebrowImage({
            side: 'right',
            icon: rightIcon,
            iconColor: rightIconColor,
            fill: fillRight,
            stroke: strokeRight,
            eyebrowImage: rightEyebrowImage,
            eyebrowImageAlt: rightEyebrowImageAlt,
            containerClassName: [
              'u-sm-paddingTop4 u-md-paddingTop8 u-paddingVert0',
              !hasBothImages &&
                hasLeftColumnImage &&
                'u-sm-paddingLeft0 u-md-paddingLeft0 u-paddingLeft40'
            ]
              .filter(Boolean)
              .join(' '),
            testId
          })}
          {renderEyebrowText({
            side: 'right',
            eyebrowText: rightEyebrowText,
            className: [
              'Type-5 u-textEyebrow u-paddingVert8 u-sm-paddingRight0 u-md-paddingRight0 u-paddingRight40',
              !hasBothImages &&
                hasLeftColumnImage &&
                'u-sm-paddingLeft0 u-md-paddingLeft0 u-paddingLeft40'
            ]
              .filter(Boolean)
              .join(' '),
            testId
          })}
          {renderInnerContent({
            side: 'right',
            containerClassName: [
              'TwoColumnBanner-innerContent u-paddingBottom8',
              !hasBothImages &&
                hasLeftColumnImage &&
                'u-sm-paddingLeft0 u-md-paddingLeft0 u-paddingLeft40'
            ]
              .filter(Boolean)
              .join(' '),
            columnContent: rightColumnContent,
            hasColumnTable: hasRightColumnTable,
            backgroundColor: rightBackgroundColor,
            textColor,
            descriptionPlaylistId: leftPlaylistId,
            descriptionContainerClassName:
              'TwoColumnBanner-videoDescriptionContainer\n                        u-sm-paddingRight0 u-md-paddingRight0 u-paddingRight40',
            playerPlaylistId: rightPlaylistId,
            testId
          })}
          {(rightPrimaryBtnUrl || rightTertiaryBtnUrl) && (
            <div
              className={[
                'u-sm-paddingBottom4 u-md-paddingBottom8 u-paddingBottom0 u-paddingTop32',
                !hasBothImages &&
                  hasLeftColumnImage &&
                  'u-sm-paddingLeft0 u-md-paddingLeft0 u-paddingLeft40',
                !hasRightColumnImage &&
                  'u-sm-paddingRight0 u-md-paddingRight0 u-paddingRight40'
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {rightPrimaryBtnUrl && (
                <Link
                  variant={LinkVariant.PRIMARY}
                  name={rightPrimaryBtnText ?? ''}
                  url={rightPrimaryBtnUrl}
                  target={rightPrimaryBtnTarget as LinkTarget}
                  analyticsName={rightPrimaryBtnAnalytics}
                  className={primaryBtnClassName}
                  data-psn-analytics={psnAnalytics}
                  testId={testId ? `${testId}-primary-right-button` : undefined}
                />
              )}
              {rightTertiaryBtnUrl && (
                <Link
                  variant={LinkVariant.TERTIARY}
                  name={rightTertiaryBtnText ?? ''}
                  url={rightTertiaryBtnUrl}
                  target={rightTertiaryBtnTarget as LinkTarget}
                  analyticsName={rightTertiaryBtnAnalytics}
                  className={tertiaryBtnClassName}
                  data-psn-analytics={psnAnalytics}
                  testId={
                    testId ? `${testId}-tertiary-right-button` : undefined
                  }
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  )

  return (
    <TwoColumnBannerShell props={props}>
      <div className={classes}>
        {leftColumn}
        {rightColumn}
      </div>
    </TwoColumnBannerShell>
  )
}
